"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AnimatePresence, motion } from 'framer-motion';
import { Spin as Hamburger } from 'hamburger-react';
import { ArrowRight, BookOpen, Box, CalendarClock, Captions, CircleHelp, CopyCheck, FileText, Gamepad, Gem, Layers3, LineChart, Newspaper, Trophy, UserCog, Waypoints } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import Icons from '../ui/icons';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import Image from "next/image";
import { Skeleton } from "../ui/skeleton";
import { signOut, useSession } from "next-auth/react";
import { roles } from "@/lib/utils";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "../ui/sheet";
import CartSheetContent from "../contents/CartSheetContent";
import { useCart } from "../providers/CartContext";

const MobileNavbar = () => {
    const { data: session, status } = useSession()
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { items } = useCart()
    const variants = {
        open: { opacity: 1, x: 0 },
        closed: { opacity: 0, x: "100%" },
    };

    useEffect(() => {
        const handleBodyOverflow = () => {
            document.body.style.overflow = isOpen ? 'hidden' : 'auto';
        };

        handleBodyOverflow();

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    return (
        <div className="relative flex w-full h-16 backdrop-blur-md lg:hidden">
            <div className="relative flex items-center justify-between w-full px-5">

                <Link href="/" className="text-lg  text-gray-300">
                    بازیتور
                </Link>

                <div className="relative flex gap-2 z-[99999] items-center">
                    <Sheet>
                        <SheetTrigger className="relative">
                            <div className='border-[#4e2b754f]  rounded-xl bg-[#3c2a4d80] p-2 border-[1px] '>
                                <Icons.cart className="w-[21px] h-[21px]" />
                            </div>
                            {items.length > 0 && (
                                <span className="absolute -top-1 -right-1 text-xs bg-purple-600 text-white rounded-full w-4 h-4 flex items-center justify-center">
                                    <p className='text-xs pt-[2px]'>{items.length}</p>
                                </span>
                            )}
                        </SheetTrigger>
                        <SheetContent className="bg-background p-4 w-full max-w-2xl z-[1000]">
                            <SheetTitle>سبد خرید</SheetTitle>
                            <CartSheetContent />
                        </SheetContent>
                    </Sheet>
                    {status === 'loading' ? (
                        <div className="flex gap-2 items-center space-x-4">
                            <Skeleton className="h-10 w-10 rounded-full" />
                        </div>
                    ) : (
                        <>
                            {status === 'authenticated' ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger className='cursor-pointer rounded-2xl p-1' asChild>
                                        <div className="flex gap-2 items-center justify-center space-x-4">
                                            <Image src={session?.user?.profile || `/images/2.png`} alt='عکس پروفایل' width={1000} height={1000} className='h-10 w-10 p-1 border-[1px] border-[#4e2b754f]  rounded-xl bg-[#3c2a4d80]' />
                                        </div>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-56 border-b-[1px] border-[#1C1B1D]">
                                        <DropdownMenuLabel>
                                            <div className="">
                                                <div className=" w-[120px] text-sm"><p>{session.user.username}</p></div>
                                                <div className=" w-[160px] text-xs"><p>بعنوان {roles[session.user.role || 'user']} وارد شدید</p></div>
                                            </div>
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuGroup>
                                            <DropdownMenuItem>
                                                پروفایل
                                            </DropdownMenuItem>
                                            <DropdownMenuItem>
                                                سفارشات
                                            </DropdownMenuItem>
                                            <DropdownMenuItem>
                                                تنظیمات
                                            </DropdownMenuItem>
                                            <DropdownMenuItem>
                                                تیکت پشتیبانی
                                            </DropdownMenuItem>
                                        </DropdownMenuGroup>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem>تلگرام</DropdownMenuItem>
                                        <DropdownMenuItem>اینستاگرام</DropdownMenuItem>
                                        <DropdownMenuItem disabled>مستندات سایت</DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => signOut()}>
                                            خروج
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <div className='flex flex-row gap-2'>
                                    <Button size="sm" variant="secondary" asChild>
                                        <Link href="/auth/signin">
                                            ورود
                                        </Link>
                                    </Button>
                                    <Button size="sm" variant="secondary" asChild>
                                        <Link href="/auth/signup">
                                            ثبت نام
                                        </Link>
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                    <Hamburger
                        toggled={isOpen}
                        toggle={setIsOpen}
                        color="#fff"
                        direction='left'
                        distance='md'
                        size={20}
                        rounded
                    />


                </div>

                <AnimatePresence presenceAffectsLayout>
                    {isOpen && (
                        <motion.div
                            initial="closed"
                            animate={isOpen ? "open" : "closed"}
                            variants={variants}
                            transition={{
                                type: "spring",
                                bounce: 0.15,
                                duration: 0.5,
                            }}
                            className="absolute z-[9999] rounded-lg w-full min-h-screen h-lvh flex flex-col inset-0 bg-background"
                        >
                            <ul className="flex flex-col items-start flex-1 w-full px-6 py-3 pt-20 space-y-3">
                                <li
                                    onClick={() => setIsOpen(false)}
                                    className="w-full px-4 py-2 text-base font-normal transition transform rounded-md cursor-pointer text-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                >
                                    <Link href="/store" className="flex items-center w-full text-start">
                                        <Gem className="w-4 h-4 ml-2" />
                                        فروشگاه بازی
                                    </Link>
                                </li>
                                <Accordion type="single" collapsible className="w-full">
                                    <AccordionItem value="item-1" className="border-transparent">
                                        <AccordionTrigger className="px-4 py-2 text-base font-normal">
                                            <span className="flex items-center">
                                                <Gamepad className="w-4 h-4 ml-2" />
                                                بازی‌ها
                                            </span>
                                        </AccordionTrigger>
                                        <AccordionContent onClick={() => setIsOpen(false)} className="flex flex-col items-start gap-1 mt-1">
                                            <li
                                                className="w-full px-4 py-2 text-sm font-normal transition transform rounded-md cursor-pointer text-muted-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                            >
                                                <Link href="/games/online" className="flex items-center w-full text-start">
                                                    <Captions className="w-4 h-4 ml-2" />
                                                    بازی‌های آنلاین
                                                </Link>
                                            </li>
                                            <li
                                                className="w-full px-4 py-2 text-sm font-normal transition transform rounded-md cursor-pointer text-muted-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                            >
                                                <Link href="/games/story-mode" className="flex items-center w-full text-start">
                                                    <CalendarClock className="w-4 h-4 ml-2" />
                                                    بازی‌های داستانی
                                                </Link>
                                            </li>
                                            <li
                                                className="w-full px-4 py-2 text-sm font-normal transition transform rounded-md cursor-pointer text-muted-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                            >
                                                <Link href="/games/racing" className="flex items-center w-full text-start">
                                                    <LineChart className="w-4 h-4 ml-2" />
                                                    بازی‌های مسابقه‌ای
                                                </Link>
                                            </li>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                                <li
                                    onClick={() => setIsOpen(false)}
                                    className="w-full px-4 py-2 text-base font-normal transition transform rounded-md cursor-pointer text-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                >
                                    <Link href="/blog" className="flex items-center w-full text-start">
                                        <Newspaper className="w-4 h-4 ml-2" />
                                        بلاگ گیمینگ
                                    </Link>
                                </li>
                                <li
                                    onClick={() => setIsOpen(false)}
                                    className="w-full px-4 py-2 text-base font-normal transition transform rounded-md cursor-pointer text-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                >
                                    <Link href="/tournaments" className="flex items-center w-full text-start">
                                        <Trophy className="w-4 h-4 ml-2" />
                                        تورنمنت‌ها
                                    </Link>
                                </li>
                                <Accordion type="single" collapsible className="w-full">
                                    <AccordionItem value="item-1" className="border-transparent">
                                        <AccordionTrigger className="px-4 py-2 text-base font-normal">
                                            <span className="flex items-center">
                                                <Layers3 className="w-4 h-4 ml-2" />
                                                منابع
                                            </span>
                                        </AccordionTrigger>
                                        <AccordionContent onClick={() => setIsOpen(false)} className="flex flex-col items-start gap-1 mt-1">
                                            <li
                                                className="w-full px-4 py-2 text-sm font-normal transition transform rounded-md cursor-pointer text-muted-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                            >
                                                <Link href="/resources/guides" className="flex items-center w-full text-start">
                                                    <BookOpen className="w-4 h-4 ml-2" />
                                                    راهنماها
                                                </Link>
                                            </li>
                                            <li
                                                className="w-full px-4 py-2 text-sm font-normal transition transform rounded-md cursor-pointer text-muted-foreground text-start active:scale-95 hover:bg-neutral-900 active:opacity-80"
                                            >
                                                <Link href="/resources/reviews" className="flex items-center w-full text-start">
                                                    <FileText className="w-4 h-4 ml-2" />
                                                    بررسی‌ها
                                                </Link>
                                            </li>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </ul>
                        </motion.div>

                    )}
                </AnimatePresence>

            </div>
        </div>
    )
};

export default MobileNavbar
